import { Component, Emit, Prop, PropSync, Vue } from 'vue-property-decorator'
import LiquorTree from 'liquor-tree'
import SynopticTemplatesModalStructureForm from '@/modules/configurationModule/detail/templates/synoptic-templates/components/modal-structure-form/configuration-templates-synoptic-templates-modal-structure-form.vue'
import {
    ConfigurationSynopticTemplateResponse,
    SynopticTemplateNode
} from '@/modules/configurationModule/detail/templates/synoptic-templates/services/configuration-synoptic-template/configuration-synoptic-template.dto'
import {
    initialConfigurationTemplatesSynopticTemplatesModalStructureState,
    defaultSynopticTemplateTreeNodeStructure,
    ConfigurationTemplatesSynopticTemplatesModalStructureState,
    SynopticTemplateTreeNode,
    SynopticTemplateTreeNodeStructure
} from '@/modules/configurationModule/detail/templates/synoptic-templates/components/modal-structure/configuration-templates-synoptic-templates-modal-structure.model'

@Component({
    components: {
        LiquorTree,
        SynopticTemplatesModalStructureForm
    }
})
export default class ConfigurationTemplatesSynopticTemplatesModalStructure extends Vue {
    @Prop(String) readonly actionTitle!: string
    @PropSync('synopticTemplate') syncedSynopticTemplate!: ConfigurationSynopticTemplateResponse

    @Emit()
    update(): void {}

    state: ConfigurationTemplatesSynopticTemplatesModalStructureState

    get synopticTemplateTreeData(): SynopticTemplateTreeNodeStructure[] {
        if (this.syncedSynopticTemplate.tree.length) {
            return ConfigurationTemplatesSynopticTemplatesModalStructure._synopticTemplateNodeToTreeNode(
                this.syncedSynopticTemplate.tree)
        }
        return [defaultSynopticTemplateTreeNodeStructure()]
    }

    get canAddChildrenInBatch(): boolean {
        return !!this.state.checkedTreeNodes.length
    }
    get canDeleteChildrenInBatch(): boolean {
        return !!this.state.checkedTreeNodes.length
    }

    get modelValidated(): boolean {
        const treeNodes = this.state.tree?.findAll<SynopticTemplateTreeNode>({}) ?? []
        const checkAllChildren = treeNodes.every(treeNode => !!treeNode.data.code.length && !!treeNode.data.text.length)
        const hasOperations = this.state.operationsCount > 0
        return hasOperations && checkAllChildren
    }

    private get _treeModel(): SynopticTemplateTreeNode[] {
        return this.state.tree?.tree.model ?? []
    }

    constructor() {
        super()
        this.state = initialConfigurationTemplatesSynopticTemplatesModalStructureState()
    }

    private static _synopticTemplateNodeToTreeNode(synopticTemplateNodes: SynopticTemplateNode[]): SynopticTemplateTreeNodeStructure[] {
        return synopticTemplateNodes.map(synopticTemplateNode => ({
            text: synopticTemplateNode.text,
            data: synopticTemplateNode,
            children: ConfigurationTemplatesSynopticTemplatesModalStructure._synopticTemplateNodeToTreeNode(
                synopticTemplateNode.children ?? [])
        } as SynopticTemplateTreeNodeStructure))
    }
    private static _treeNodeToSynopticTemplateNode(treeNodes: SynopticTemplateTreeNode[]): SynopticTemplateNode[] {
        return treeNodes.map(treeNode => ({
            ...treeNode.data,
            children: ConfigurationTemplatesSynopticTemplatesModalStructure._treeNodeToSynopticTemplateNode(
                treeNode.children)
        } as SynopticTemplateNode))
    }

    /// TREE EVENT HANDLERS ///
    onTreeMounted(tree: LiquorTree): void {
        this.state.tree = tree
        this.state.tree.expandAll()
    }
    updateCheckedTreeNodes(): void {
        const treeNodes: SynopticTemplateTreeNode[] = this.state.tree?.checked<SynopticTemplateTreeNode>() ?? []
        const rootNodes = this._treeModel.filter(treeNode => treeNodes.includes(treeNode))
        const leafNodes = treeNodes.filter(treeNode => treeNode.depth > 0)
        const reverseRootNodes = [...rootNodes].reverse()
        this.state.checkedTreeNodes = [...reverseRootNodes, ...leafNodes]
    }
    updateSelectedTreeNode(): void {
        const treeNodes = this.state.tree?.selected<SynopticTemplateTreeNode>() ?? []
        this.state.selectedTreeNode = treeNodes.length ? treeNodes[0] : null
    }

    /// OPERATIONS ///

    onTreeNodeChanged(): void {
        ++this.state.operationsCount
    }

    addChildrenInBatch(): void {
        this.state.checkedTreeNodes.forEach(treeNode => {
            treeNode.append(defaultSynopticTemplateTreeNodeStructure())
            treeNode.expand()
        })
    }

    private async _showModalAdviceWhenDeleteInBatch(): Promise<boolean> {
        const labelText = this.$tc('configuration.modal.synopticTemplate.deleteModalConfirmationInBatch')
        const labelDeleteQuestion = this.$tc('configuration.grid.deleteQuestion')
        const labelCancelAction = this.$tc('component.modalConfirmation.okTitle')
        const labelAcceptAction = this.$tc('component.modalConfirmation.cancelTitle')
        return this.$_showConfirmationModal(
            `${labelText}, ${labelDeleteQuestion}`,
            labelCancelAction,
            labelAcceptAction
        )
    }
    private _canDeleteTreeNode(treeNode: SynopticTemplateTreeNode): boolean {
        if (treeNode.isRoot()) {
            const rootNodes = this._treeModel.filter(treeNode => treeNode.isRoot())
            return rootNodes.length > 1
        }
        return true
    }
    async removeChildrenInBatch(): Promise<void> {
        if (await this._showModalAdviceWhenDeleteInBatch()) {
            this.state.checkedTreeNodes.forEach(treeNode => {
                if (!this._canDeleteTreeNode(treeNode)) {
                    this.$_showWarningModal(this.$tc('configuration.modal.synopticTemplate.deleteModalErrorNode'))

                    treeNode.uncheck()
                    treeNode.unselect()
                } else {
                    treeNode.remove()
                }
            })
        }
    }

    save(): void {
        this.syncedSynopticTemplate.tree =
            ConfigurationTemplatesSynopticTemplatesModalStructure._treeNodeToSynopticTemplateNode(this._treeModel)
        this.update() // emits an event
    }
}
